import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadPDFGuide } from '../utils/downloadUtils';
import { serviceAPI } from '../services/api';
import { useApi } from '../hooks/useApi';
import Navbar from './Navbar';
import Footer from './Footer';

const ServicesPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Fetch services from backend
  const { data: services, loading: servicesLoading, error: servicesError, refetch } = useApi(
    serviceAPI.getPublicServices,
    []
  );

  // Transform backend services to match frontend format
  const transformedServices = services ? services.map(service => ({
    id: service.id,
    category: service.category?.toLowerCase() || 'other',
    title: service.name,
    description: service.description,
    icon: getCategoryIcon(service.category),
    status: service.featured ? 'POPULAR' : service.status === 'BETA' ? 'BETA' : null,
    statusColor: service.featured ? 'primary' : service.status === 'BETA' ? 'primary' : null,
    categoryLabel: service.category || 'Other',
    categoryColor: getCategoryColor(service.category),
    price: service.price,
    pricingModel: service.pricingModel
  })) : [];

  function getCategoryIcon(category) {
    const iconMap = {
      'HOSPITALITY': 'restaurant',
      'MEDIA': 'videocam',
      'SAAS': 'support_agent',
      'ARCHITECTURE': 'database',
      'GROWTH': 'campaign',
      'AUTOMATION': 'smart_toy',
      'ANALYTICS': 'analytics',
      'INTEGRATION': 'hub'
    };
    return iconMap[category] || 'apps';
  }

  function getCategoryColor(category) {
    const colorMap = {
      'HOSPITALITY': 'amber-500',
      'MEDIA': 'green-500',
      'SAAS': 'green-500',
      'ARCHITECTURE': 'slate-600',
      'GROWTH': 'green-500',
      'AUTOMATION': 'blue-500',
      'ANALYTICS': 'purple-500',
      'INTEGRATION': 'indigo-500'
    };
    return colorMap[category] || 'slate-600';
  }

  const categories = [
    { id: 'all', name: 'All Modules', icon: 'hub' },
    { id: 'hospitality', name: 'Retail & Dining', icon: 'storefront' },
    { id: 'media', name: 'Media Synthesis', icon: 'movie_edit' },
    { id: 'saas', name: 'Autonomous Support', icon: 'smart_toy' },
    { id: 'architecture', name: 'Core Intelligence', icon: 'memory' },
    { id: 'growth', name: 'Growth Engines', icon: 'auto_graph' },
    { id: 'automation', name: 'Automation', icon: 'precision_manufacturing' },
    { id: 'analytics', name: 'Analytics', icon: 'analytics' },
    { id: 'integration', name: 'Integration', icon: 'hub' }
  ];

  const filteredServices = selectedCategory === 'all' 
    ? transformedServices 
    : transformedServices.filter(service => service.category === selectedCategory);

  const handleDeployService = async (serviceId) => {
    setLoading(true);
    setError('');
    try {
      await serviceAPI.deployService(serviceId);
      // Show success message or redirect
      navigate(`/services/${serviceId}?deployed=true`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deploy service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#0a1116', color: 'white', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }} className="relative overflow-hidden">
      {/* Background orbs */}
      <div className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(19,164,236,0.06) 0%, transparent 70%)' }}></div>
      <div className="absolute bottom-40 right-20 w-64 h-64 rounded-full blur-2xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 70%)' }}></div>

      {/* Shared Navbar */}
      <Navbar variant="services" />

      <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row min-h-[calc(100vh-65px)]">
        {/* Sidebar */}
        <aside className={`w-full md:w-64 border-r border-[#233c48] p-6 flex flex-col gap-8 shrink-0 transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}`}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <h1 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em]">Operating System</h1>
              <p className="text-slate-300 text-sm font-bold mt-1">Service Categories</p>
            </div>
            <nav className="flex flex-col gap-1">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    if (category.id === 'hospitality') {
                      navigate('/services/retail-dining');
                    } else if (category.id === 'media') {
                      navigate('/services/media-synthesis');
                    } else if (category.id === 'architecture') {
                      navigate('/services/core-intelligence');
                    } else if (category.id === 'saas') {
                      navigate('/services/autonomous-support');
                    } else if (category.id === 'growth') {
                      navigate('/services/growth-engines');
                    } else {
                      setSelectedCategory(category.id);
                    }
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                    selectedCategory === category.id
                      ? 'bg-[#13a4ec] text-white shadow-lg shadow-[#13a4ec]/20'
                      : 'hover:bg-[#233c48] text-slate-400 hover:text-white'
                  }`}
                >
                  <span className="material-symbols-outlined text-[20px]">{category.icon}</span>
                  <p className="text-sm font-medium">{category.name}</p>
                  {['hospitality', 'media', 'architecture', 'saas', 'growth'].includes(category.id) && (
                    <span className="material-symbols-outlined text-xs ml-auto">arrow_forward</span>
                  )}
                </button>
              ))}
            </nav>
          </div>
          
          {/* Enterprise Badge */}
          <div className="flex flex-col gap-4 mt-auto">
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#1a2b34] to-[#0a1116] border border-[#233c48]">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-[#13a4ec] text-sm">verified</span>
                <p className="text-[10px] font-bold text-[#13a4ec] uppercase tracking-wider">OrbitMind Enterprise</p>
              </div>
              <p className="text-xs text-slate-400 mb-4">Unlimited scaling enabled for all active AI modules.</p>
              <button className="w-full py-2 bg-[#233c48] rounded-lg text-xs font-bold hover:bg-[#13a4ec]/20 hover:text-[#13a4ec] transition-all">
                Billing Portal
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col bg-transparent">
          {/* Breadcrumbs */}
          <div className="px-6 py-4 flex items-center gap-2 text-[10px] uppercase tracking-widest">
            <button onClick={() => navigate('/')} className="text-slate-500 hover:text-[#13a4ec] transition-colors font-bold">OrbitMind</button>
            <span className="text-slate-700">/</span>
            <span className="text-slate-300 font-bold">Services Directory</span>
          </div>

          {/* Page Header */}
          <div className="px-6 py-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-500">
                Service Directory
              </h2>
              <p className="text-slate-500 mt-2 text-sm font-medium">
                Centralized control for your enterprise automation ecosystem.
              </p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => downloadPDFGuide('Services Overview')}
                className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 transition-all"
              >
                <span className="material-symbols-outlined text-[18px]">download</span>
                Resources
              </button>
              <button 
                onClick={() => navigate('/deploy-module')}
                className="bg-[#13a4ec] hover:bg-[#13a4ec]/90 text-white px-5 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 shadow-lg shadow-[#13a4ec]/30 transition-all active:scale-95"
              >
                <span className="material-symbols-outlined text-[18px]">add_circle</span>
                Deploy New Module
              </button>
            </div>
          </div>

          {/* Service Category Quick Access */}
          <div className="px-6 py-4">
            <h3 className="text-lg font-bold text-white mb-4">Featured Service Categories</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
              <button
                onClick={() => navigate('/services/retail-dining')}
                className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-lg p-4 text-left hover:border-amber-500/50 transition-all group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-amber-500 text-xl">storefront</span>
                  <span className="text-white font-semibold text-sm">Retail & Dining</span>
                </div>
                <p className="text-slate-400 text-xs">Restaurant & retail automation</p>
              </button>
              
              <button
                onClick={() => navigate('/services/media-synthesis')}
                className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-4 text-left hover:border-purple-500/50 transition-all group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-purple-400 text-xl">movie_edit</span>
                  <span className="text-white font-semibold text-sm">Media Synthesis</span>
                </div>
                <p className="text-slate-400 text-xs">AI content creation platform</p>
              </button>
              
              <button
                onClick={() => navigate('/services/core-intelligence')}
                className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-lg p-4 text-left hover:border-blue-500/50 transition-all group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-blue-400 text-xl">memory</span>
                  <span className="text-white font-semibold text-sm">Core Intelligence</span>
                </div>
                <p className="text-slate-400 text-xs">Neural processing engine</p>
              </button>
              
              <button
                onClick={() => navigate('/services/autonomous-support')}
                className="bg-gradient-to-br from-green-500/10 to-teal-500/10 border border-green-500/20 rounded-lg p-4 text-left hover:border-green-500/50 transition-all group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-green-400 text-xl">smart_toy</span>
                  <span className="text-white font-semibold text-sm">Autonomous Support</span>
                </div>
                <p className="text-slate-400 text-xs">AI customer service platform</p>
              </button>
              
              <button
                onClick={() => navigate('/services/growth-engines')}
                className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-lg p-4 text-left hover:border-orange-500/50 transition-all group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-orange-400 text-xl">auto_graph</span>
                  <span className="text-white font-semibold text-sm">Growth Engines</span>
                </div>
                <p className="text-slate-400 text-xs">Business growth acceleration</p>
              </button>

              <button
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-lg p-4 text-left hover:border-emerald-500/50 transition-all group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-emerald-400 text-xl">account_balance</span>
                  <span className="text-white font-semibold text-sm">Billing Hub</span>
                </div>
                <p className="text-slate-400 text-xs">Manage plans & billing</p>
              </button>
            </div>
          </div>

          {/* Filter Controls */}
          <div className="px-6 py-2 flex gap-3 flex-wrap items-center">
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mr-2">Filter Engine:</span>
            <button className="flex h-8 items-center gap-x-2 rounded-full border border-[#233c48] bg-transparent px-4 hover:border-[#13a4ec]/50 transition-colors">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-300">Popularity</p>
              <span className="material-symbols-outlined text-[14px] text-slate-500">expand_more</span>
            </button>
            <button className="flex h-8 items-center gap-x-2 rounded-full border border-[#233c48] bg-transparent px-4 hover:border-[#13a4ec]/50 transition-colors">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-300">Instance Type</p>
              <span className="material-symbols-outlined text-[14px] text-slate-500">expand_more</span>
            </button>
            <button className="flex h-8 items-center gap-x-2 rounded-full border border-[#13a4ec]/50 bg-[#13a4ec]/10 px-4 transition-colors text-[#13a4ec]">
              <p className="text-[10px] font-bold uppercase tracking-wide">Live Nodes Only</p>
              <span className="material-symbols-outlined text-[14px]">sensors</span>
            </button>
            <div className="h-4 w-px bg-[#233c48] mx-2"></div>
            <button className="text-[10px] font-bold text-slate-500 hover:text-white transition-colors uppercase">Reset</button>
          </div>

          {/* Services Grid */}
          <div className="p-6">
            {/* Loading State */}
            {servicesLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 border-2 border-[#13a4ec]/30 border-t-[#13a4ec] rounded-full animate-spin"></div>
                  <span className="text-slate-400">Loading services...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {servicesError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-red-400">error</span>
                  <div>
                    <p className="text-red-400 font-medium">Failed to load services</p>
                    <p className="text-red-300 text-sm mt-1">{servicesError}</p>
                  </div>
                </div>
                <button 
                  onClick={refetch}
                  className="mt-4 bg-red-500/20 hover:bg-red-500/30 text-red-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Services Grid */}
            {!servicesLoading && !servicesError && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredServices.map((service) => (
                  <div 
                    key={service.id} 
                    className="group flex flex-col rounded-xl border border-[#233c48] bg-[#16252d] overflow-hidden hover:border-[#13a4ec]/50 hover:shadow-2xl hover:shadow-[#13a4ec]/5 transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/services/${service.id}`)}
                  >
                    <div className="h-40 bg-gradient-to-br from-[#0a1116] to-[#16252d] flex items-center justify-center relative border-b border-[#233c48]">
                      <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity"></div>
                      <span className="material-symbols-outlined text-[#13a4ec] text-5xl opacity-80 group-hover:scale-110 transition-transform">
                        {service.icon}
                      </span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-bold text-lg text-slate-100">{service.title}</h3>
                        {service.status && (
                          <span className={`text-[9px] px-2 py-0.5 rounded-full font-black tracking-widest uppercase ${
                            service.statusColor === 'primary' 
                              ? 'bg-[#13a4ec]/10 text-[#13a4ec] border border-[#13a4ec]/20'
                              : service.statusColor === 'green-500'
                              ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                              : ''
                          }`}>
                            {service.status}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm leading-relaxed mb-4 flex-1">
                        {service.description}
                      </p>
                      {service.price && (
                        <div className="mb-4 p-3 bg-[#233c48]/30 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500 uppercase tracking-wider">Pricing</span>
                            <span className="text-[#13a4ec] font-bold">
                              ${service.price}
                              {service.pricingModel === 'MONTHLY' && '/mo'}
                              {service.pricingModel === 'USAGE_BASED' && '/use'}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-[#233c48]">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${
                            service.categoryColor === 'amber-500' ? 'bg-amber-500 animate-pulse' :
                            service.categoryColor === 'green-500' ? 'bg-green-500' :
                            service.categoryColor === 'blue-500' ? 'bg-blue-500' :
                            service.categoryColor === 'purple-500' ? 'bg-purple-500' :
                            service.categoryColor === 'indigo-500' ? 'bg-indigo-500' :
                            'bg-slate-600'
                          }`}></span>
                          <span className="text-[10px] font-bold text-slate-500 uppercase">{service.categoryLabel}</span>
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeployService(service.id);
                          }}
                          disabled={loading}
                          className="text-[#13a4ec] hover:text-white hover:bg-[#13a4ec] disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-xs font-bold border border-[#13a4ec]/30 transition-all"
                        >
                          {loading ? 'Deploying...' : 'Deploy'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Custom Module Card */}
                <div className="group flex flex-col rounded-xl border border-dashed border-[#233c48] bg-transparent overflow-hidden hover:bg-white/5 transition-all duration-300 items-center justify-center p-8 text-center min-h-[300px]">
                  <div className="w-16 h-16 rounded-full bg-[#233c48]/50 flex items-center justify-center mb-4 border border-slate-700">
                    <span className="material-symbols-outlined text-slate-500 text-3xl">terminal</span>
                  </div>
                  <h3 className="font-bold text-lg text-slate-300">Custom Module</h3>
                  <p className="text-slate-500 text-xs max-w-[200px] mt-2 font-medium">
                    Bridge your proprietary APIs with OrbitMind's orchestration layer.
                  </p>
                  <button className="mt-6 border border-slate-600 hover:border-[#13a4ec] text-slate-500 hover:text-[#13a4ec] px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all">
                    Open Developer Portal
                  </button>
                </div>

                {/* Empty State */}
                {filteredServices.length === 0 && !servicesLoading && (
                  <div className="col-span-full flex flex-col items-center justify-center py-12">
                    <span className="material-symbols-outlined text-slate-500 text-6xl mb-4">search_off</span>
                    <h3 className="text-slate-300 font-bold text-lg mb-2">No services found</h3>
                    <p className="text-slate-500 text-sm mb-4">Try selecting a different category or check back later.</p>
                    <button 
                      onClick={() => setSelectedCategory('all')}
                      className="text-[#13a4ec] hover:underline text-sm font-medium"
                    >
                      View all services
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Pagination bar */}
          <div className="mt-auto px-6 py-6 border-t border-[#233c48] flex flex-col sm:flex-row items-center justify-between gap-4" style={{ backgroundColor: '#0a1116' }}>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Active Modules: {filteredServices.length} / 24 Capacity
            </p>
            <div className="flex gap-2">
              <button className="w-9 h-9 rounded-lg border border-[#233c48] flex items-center justify-center text-slate-500 hover:text-white hover:bg-[#233c48] transition-colors">
                <span className="material-symbols-outlined text-[18px]">chevron_left</span>
              </button>
              <button className="w-9 h-9 rounded-lg text-white font-black text-xs" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>01</button>
              <button className="w-9 h-9 rounded-lg border border-[#233c48] flex items-center justify-center text-slate-500 hover:text-white hover:bg-[#233c48] transition-colors text-xs font-bold">02</button>
              <button className="w-9 h-9 rounded-lg border border-[#233c48] flex items-center justify-center text-slate-500 hover:text-white hover:bg-[#233c48] transition-colors text-xs font-bold">03</button>
              <button className="w-9 h-9 rounded-lg border border-[#233c48] flex items-center justify-center text-slate-500 hover:text-white hover:bg-[#233c48] transition-colors">
                <span className="material-symbols-outlined text-[18px]">chevron_right</span>
              </button>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default ServicesPage;